import { Star, ExternalLink } from 'lucide-react'

// StarRating komponent för att visa halva stjärnor
function StarRating({ rating, size = 'w-4 h-4' }: { rating: number; size?: string }) {
  const stars = []
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.5

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      // Full stjärna
      stars.push(
        <Star key={i} className={`${size} text-gold-400 fill-gold-400`} />
      )
    } else if (i === fullStars && hasHalfStar) {
      // Halv stjärna - använder en maskad version
      stars.push(
        <div key={i} className="relative">
          {/* Bakgrund (grå stjärna) */}
          <Star className={`${size} text-gray-600`} />
          {/* Förgrund (gold stjärna - maskad till 50%) */}
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star className={`${size} text-gold-400 fill-gold-400`} />
          </div>
        </div>
      )
    } else {
      // Tom stjärna
      stars.push(
        <Star key={i} className={`${size} text-gray-600`} />
      )
    }
  }

  return <div className="flex items-center gap-0.5">{stars}</div>
}

// Data från Bokadirekt - uppdatera manuellt eller via scraping
const reviewsData = {
  averageRating: 4.5,
  totalReviews: 222,
  source: 'Bokadirekt',
  sourceUrl: 'https://www.bokadirekt.se/places/vk-studio-54503',
}

const featuredReviews = [
  {
    id: 1,
    name: 'Adam A.',
    rating: 5,
    date: 'för ungefär en månad sedan',
    text: 'Blev lycklig med resultatet, rekommenderar självklart till andra.',
    service: 'Herrklippning',
    staff: 'Valton',
  },
  {
    id: 2,
    name: 'Maria B.',
    rating: 5,
    date: 'för 4 månader sedan',
    text: 'Klippningen blev precis som mina barn tänkt sig. Det gick snabbt och blev snygga frisyrer till ett överkomligt pris. Riktigt duktig frisör! Jätte smidigt att boka tid via hemsidan. Vi kommer att komma tillbaka.',
    service: 'Barn Klippning',
    staff: 'Valton',
  },
  {
    id: 3,
    name: 'Marcus F.',
    rating: 5,
    date: 'för 11 månader sedan',
    text: 'Superbra bemötande och kan sin sak! Stilren salong med toppbetyg som frisör/barberare! Jag rekommenderar verkligen!',
    service: 'Herrklippning',
    staff: 'Valton',
  },
]

export default function Reviews() {
  return (
    <section id="recensioner" className="relative py-24 lg:py-32 bg-dark-800 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-gold-400 font-semibold text-sm tracking-wider uppercase mb-4">
            Recensioner
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
            Vad våra <span className="text-gradient">kunder</span> säger
          </h2>
          
          {/* Rating Summary */}
          <div className="inline-flex items-center gap-4 px-8 py-4 rounded-2xl glass">
            <div className="flex items-center gap-1">
              {/* Rendera stjärnor med halv stjärna support */}
              <StarRating rating={reviewsData.averageRating} size="w-6 h-6" />
            </div>
            <div className="text-left">
              <div className="text-2xl font-bold text-white">{reviewsData.averageRating}</div>
              <div className="text-sm text-gray-400">av 5 • {reviewsData.totalReviews} betyg</div>
            </div>
            <div className="w-px h-12 bg-white/10 mx-2" />
            <a
              href={reviewsData.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gold-400 hover:text-gold-300 transition-colors text-sm"
            >
              <span>Se på {reviewsData.source}</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {featuredReviews.map((review) => (
            <div
              key={review.id}
              className="p-6 rounded-2xl glass hover-lift"
            >
              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <StarRating rating={review.rating} size="w-4 h-4" />
                <span className="text-sm text-gray-400">{review.date}</span>
              </div>

              {/* Review Text */}
              <p className="text-gray-300 mb-4 leading-relaxed">
                "{review.text}"
              </p>

              {/* Reviewer Info */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-white">{review.name}</div>
                  <div className="text-sm text-gold-400">{review.service}</div>
                </div>
                <div className="text-xs text-gray-500">
                  till {review.staff}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href={reviewsData.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-dark-900 font-bold rounded-full hover:from-gold-400 hover:to-gold-500 transition-all hover:scale-105 shadow-xl shadow-gold-500/25"
          >
            Lämna ett betyg på Bokadirekt
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  )
}
